/**
 * BoardV3 Drag Info Overlay
 * Shows position info and gap indicators during drag
 */

import React, { memo } from 'react';
import type { Task } from '../../types';
import type { ScheduledTask } from '../scheduler/types';
import {
  calculateGapsAtPosition,
  DAYS_PER_WEEK,
} from '../utils/boardHelpers';
import { businessDayIndexToDate, getKwFromDate } from '../../lib/dateUtils';
import { useColumnYOffsets, useDayWidth, useHeaderHeight, useTasksByColumn } from './BoardLayoutContext';
import { useBoardStartDate } from '../../stores/hooks';

interface BoardV3DragInfoOverlayProps {
  task: Task;
  targetColumn: string;
  targetDay: number;
  scheduledTasks: Map<string, ScheduledTask>;
}

export const BoardV3DragInfoOverlay = memo(function BoardV3DragInfoOverlay({
  task,
  targetColumn,
  targetDay,
  scheduledTasks,
}: BoardV3DragInfoOverlayProps) {
  const columnYOffsets = useColumnYOffsets();
  const dayWidth = useDayWidth();
  const headerHeight = useHeaderHeight();
  const tasksByColumn = useTasksByColumn();
  const boardStartDate = useBoardStartDate();

  const duration = scheduledTasks.get(task.id)?.effectiveDays ?? 10;
  const yOffset = columnYOffsets[targetColumn] ?? 0;
  const x = targetDay * dayWidth;
  const y = headerHeight + yOffset;
  const width = duration * dayWidth;

  // Calculate KW info with proper year handling
  const taskEndDay = targetDay + duration - 1;

  const startKwInfo = getKwFromDate(businessDayIndexToDate(targetDay, boardStartDate));
  const endKwInfo = getKwFromDate(businessDayIndexToDate(taskEndDay, boardStartDate));

  const startDayInWeek = targetDay % DAYS_PER_WEEK;
  const endDayInWeek = taskEndDay % DAYS_PER_WEEK;
  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];

  // Calculate gaps
  const gaps = calculateGapsAtPosition(task, targetColumn, targetDay, tasksByColumn, scheduledTasks, boardStartDate);

  return (
    <>
      {/* Info panel above the snap indicator */}
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y - 50,
          minWidth: Math.max(width, 120),
          backgroundColor: '#1e293b',
          color: '#fff',
          borderRadius: 6,
          padding: '6px 10px',
          fontSize: 11,
          pointerEvents: 'none',
          zIndex: 100,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Date range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>
            {dayNames[startDayInWeek]} KW{startKwInfo.kw}
            {startKwInfo.year !== endKwInfo.year && (
              <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 2 }}>
                '{String(startKwInfo.year).slice(-2)}
              </span>
            )}
          </span>
          <span style={{ color: '#94a3b8' }}>→</span>
          <span style={{ fontWeight: 600 }}>
            {dayNames[endDayInWeek]} KW{endKwInfo.kw}
            {endKwInfo.year !== startKwInfo.year && (
              <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 2 }}>
                '{String(endKwInfo.year).slice(-2)}
              </span>
            )}
          </span>
          <span style={{ color: '#64748b', marginLeft: 'auto' }}>
            ({duration}d)
          </span>
        </div>

        {/* Column */}
        <div style={{ fontSize: 10, color: '#94a3b8' }}>
          {targetColumn}
        </div>
      </div>

      {/* Left gap indicator */}
      {gaps.leftGap !== null && gaps.leftGap >= 0 && (
        <GapIndicator direction="left" gap={gaps.leftGap} x={x} y={y} />
      )}

      {/* Right gap indicator */}
      {gaps.rightGap !== null && gaps.rightGap >= 0 && (
        <GapIndicator direction="right" gap={gaps.rightGap} x={x + width} y={y} />
      )}
    </>
  );
});

// Unified gap indicator for both left and right directions
function GapIndicator({
  direction,
  gap,
  x,
  y,
}: {
  direction: 'left' | 'right';
  gap: number;
  x: number;
  y: number;
}) {
  const color = gap === 0 ? '#ef4444' : '#22c55e';
  const label = gap === 0 ? 'Direkt' : `${gap}d Abstand`;
  const isLeft = direction === 'left';

  const badgeStyle: React.CSSProperties = {
    backgroundColor: color,
    color: '#fff',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  };

  const arrowStyle: React.CSSProperties = {
    width: 0,
    height: 0,
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    ...(isLeft
      ? { borderLeft: `8px solid ${color}` }
      : { borderRight: `8px solid ${color}` }),
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: isLeft ? x - 8 : x + 8,
        top: y,
        transform: isLeft ? 'translateX(-100%)' : undefined,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        pointerEvents: 'none',
        zIndex: 100,
      }}
    >
      {isLeft ? (
        <>
          <div style={badgeStyle}>{label}</div>
          <div style={arrowStyle} />
        </>
      ) : (
        <>
          <div style={arrowStyle} />
          <div style={badgeStyle}>{label}</div>
        </>
      )}
    </div>
  );
}
